import React, { useState, useMemo, useEffect } from 'react';
import { Country, State, City } from 'country-state-city';
import { FiChevronDown, FiSearch, FiX } from 'react-icons/fi';

interface LocationPickerProps {
  country: string;
  state: string;
  city: string;
  onCountryChange: (code: string, name: string) => void;
  onStateChange: (code: string, name: string) => void;
  onCityChange: (name: string) => void;
}

interface DropdownItem {
  code: string;
  name: string;
}

const DropdownSelect: React.FC<{
  label: string;
  value: string;
  items: DropdownItem[];
  onChange: (code: string, name: string) => void;
  disabled?: boolean;
  placeholder?: string;
}> = ({ label, value, items, onChange, disabled, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((i) => i.name.toLowerCase().includes(q));
  }, [items, search]);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => !disabled && setOpen(true)}
        className={`w-full h-14 px-4 bg-gray-100 rounded-xl text-sm text-left flex items-center justify-between outline-none ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        } ${value ? 'text-gray-900' : 'text-gray-400'}`}
      >
        <span className="truncate">{value || placeholder || label}</span>
        <FiChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => { setOpen(false); setSearch(''); }} />
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl max-h-[70vh] flex flex-col safe-area-bottom animate-slide-up">
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-900">Select {label}</h3>
              <button
                onClick={() => { setOpen(false); setSearch(''); }}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100"
              >
                <FiX className="w-4 h-4 text-gray-600" />
              </button>
            </div>
            <div className="px-4 py-2">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={`Search ${label.toLowerCase()}...`}
                  autoFocus
                  className="w-full h-10 pl-9 pr-4 bg-gray-100 rounded-lg text-sm text-gray-900 placeholder-gray-400 outline-none"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 pb-4">
              {filtered.length > 0 ? (
                filtered.map((item) => (
                  <button
                    key={item.code}
                    onClick={() => {
                      onChange(item.code, item.name);
                      setOpen(false);
                      setSearch('');
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg text-sm hover:bg-gray-50 active:bg-gray-100 transition-colors ${
                      value === item.name ? 'text-[#39377F] font-semibold bg-[#39377F]/5' : 'text-gray-700'
                    }`}
                  >
                    {item.name}
                  </button>
                ))
              ) : (
                <p className="text-center text-gray-400 text-sm py-8">No results found</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const LocationPicker: React.FC<LocationPickerProps> = ({
  country,
  state,
  city,
  onCountryChange,
  onStateChange,
  onCityChange,
}) => {
  const [countryCode, setCountryCode] = useState('');
  const [stateCode, setStateCode] = useState('');

  // Resolve initial codes from names
  useEffect(() => {
    if (country && !countryCode) {
      const found = Country.getAllCountries().find(
        (c) => c.name.toLowerCase() === country.toLowerCase()
      );
      if (found) setCountryCode(found.isoCode);
    }
  }, [country, countryCode]);

  useEffect(() => {
    if (state && countryCode && !stateCode) {
      const found = State.getStatesOfCountry(countryCode).find(
        (s) => s.name.toLowerCase() === state.toLowerCase()
      );
      if (found) setStateCode(found.isoCode);
    }
  }, [state, countryCode, stateCode]);

  const countries: DropdownItem[] = useMemo(
    () => Country.getAllCountries().map((c) => ({ code: c.isoCode, name: c.name })),
    []
  );

  const states: DropdownItem[] = useMemo(
    () =>
      countryCode
        ? State.getStatesOfCountry(countryCode).map((s) => ({ code: s.isoCode, name: s.name }))
        : [],
    [countryCode]
  );

  const cities: DropdownItem[] = useMemo(
    () =>
      countryCode && stateCode
        ? City.getCitiesOfState(countryCode, stateCode).map((c) => ({ code: c.name, name: c.name }))
        : [],
    [countryCode, stateCode]
  );

  return (
    <div className="space-y-3">
      <DropdownSelect
        label="Country"
        value={country}
        items={countries}
        onChange={(code, name) => {
          setCountryCode(code);
          setStateCode('');
          onCountryChange(code, name);
          onStateChange('', '');
          onCityChange('');
        }}
        placeholder="Select Country"
      />
      <div className="grid grid-cols-2 gap-3">
        <DropdownSelect
          label="State"
          value={state}
          items={states}
          onChange={(code, name) => {
            setStateCode(code);
            onStateChange(code, name);
            onCityChange('');
          }}
          disabled={!countryCode}
          placeholder="Select State"
        />
        <DropdownSelect
          label="City"
          value={city}
          items={cities}
          onChange={(_, name) => onCityChange(name)}
          disabled={!stateCode}
          placeholder="Select City"
        />
      </div>
    </div>
  );
};

export default LocationPicker;
